import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, Camera, Clock, Battery, Signal, Thermometer,
  Image, Video, HardDrive, Loader2, Play,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useDirectionStore } from '@/store/directionStore'
import { testCamerasApi } from '@/api/endpoints'
import type { TestCamera, TestMediaUpload } from '@/types'

function formatTime(dateStr: string): string {
  const d = new Date(dateStr)
  const now = Date.now()
  const diff = now - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—'
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${bytes} B`
}

function displayFilename(upload: TestMediaUpload): string {
  // If S3 key has a different extension (e.g. converted .mov → .mp4), show that
  if (upload.s3_key) {
    const s3Name = upload.s3_key.split('/').pop() || ''
    return s3Name
  }
  return upload.original_filename
}

// ---------------------------------------------------------------------------
// Camera List View
// ---------------------------------------------------------------------------

function CameraList({ onSelect }: { onSelect: (imei: string) => void }) {
  const { t } = useTranslation('cameras')

  const { data: cameras = [], isLoading } = useQuery({
    queryKey: ['test-cameras'],
    queryFn: () => testCamerasApi.list(),
    staleTime: 30_000,
  })

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-4 space-y-3">
              <div className="shimmer h-4 w-32 rounded" />
              <div className="shimmer h-3 w-24 rounded" />
              <div className="shimmer h-3 w-20 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (cameras.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-48 flex-col items-center justify-center text-center gap-2">
          <Camera className="h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{t('test.noCameras')}</p>
          <p className="text-xs text-muted-foreground/60">{t('test.noCamerasHint')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cameras.map((cam: TestCamera) => (
        <Card
          key={cam.imei}
          className="overflow-hidden cursor-pointer hover:ring-1 hover:ring-primary/40 transition-all"
          onClick={() => onSelect(cam.imei)}
        >
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" />
              <CardTitle className="text-sm font-semibold truncate">{cam.imei}</CardTitle>
            </div>
            {cam.firmware_version && (
              <p className="text-[11px] text-muted-foreground mt-0.5">FW: {cam.firmware_version}</p>
            )}
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] text-muted-foreground">Uploads</p>
                <p className="text-xs font-semibold">{cam.upload_count}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Battery</p>
                <p className="text-xs font-semibold">{cam.latest_battery != null ? `${cam.latest_battery}%` : '—'}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Signal</p>
                <p className="text-xs font-semibold">{cam.latest_signal != null ? `${cam.latest_signal}/5` : '—'}</p>
              </div>
            </div>

            {/* Last seen */}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{t('test.lastSeen')}: {formatTime(cam.last_seen)}</span>
            </div>

            {/* Latest upload */}
            {cam.latest_upload && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {cam.latest_upload.media_type === 'video' ? <Video className="h-3 w-3" /> : <Image className="h-3 w-3" />}
                <span className="truncate">{cam.latest_upload.original_filename}</span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Camera Detail View — media gallery + status history
// ---------------------------------------------------------------------------

function CameraDetail({ imei, onBack }: { imei: string; onBack: () => void }) {
  const { t } = useTranslation('cameras')
  const d = useDirectionStore((s) => s.direction)
  const [selectedMedia, setSelectedMedia] = useState<TestMediaUpload | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['test-camera-detail', imei],
    queryFn: () => testCamerasApi.detail(imei),
    staleTime: 30_000,
  })

  const camera = data?.camera
  const uploads = data?.uploads ?? []
  const statuses = data?.statuses ?? []

  return (
    <div className="space-y-4">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h3 className="text-sm font-semibold">{imei}</h3>
          {camera?.firmware_version && (
            <p className="text-xs text-muted-foreground">FW: {camera.firmware_version}</p>
          )}
        </div>
        {camera && (
          <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            {camera.latest_battery != null && (
              <span className="flex items-center gap-1"><Battery className="h-3 w-3" />{camera.latest_battery}%</span>
            )}
            {camera.latest_signal != null && (
              <span className="flex items-center gap-1"><Signal className="h-3 w-3" />{camera.latest_signal}/5</span>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Media viewer modal */}
          {selectedMedia && selectedMedia.url && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
              onClick={() => setSelectedMedia(null)}
            >
              <div className="max-w-4xl max-h-[85vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
                {selectedMedia.media_type === 'video' ? (
                  <video
                    src={selectedMedia.url}
                    controls
                    autoPlay
                    className="w-full max-h-[85vh] rounded-lg"
                  />
                ) : (
                  <img
                    src={selectedMedia.url}
                    alt={displayFilename(selectedMedia)}
                    className="w-full max-h-[85vh] object-contain rounded-lg"
                  />
                )}
                <div className="mt-2 text-white/80 text-xs space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span>{displayFilename(selectedMedia)}</span>
                    <span>{formatBytes(selectedMedia.file_size)}</span>
                  </div>
                  <div className="flex items-center justify-between text-white/50">
                    {selectedMedia.camera_time && (
                      <span>Captured: {new Date(selectedMedia.camera_time).toLocaleString()}</span>
                    )}
                    <span>Uploaded: {new Date(selectedMedia.server_time).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Media grid */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Image className="h-4 w-4 text-muted-foreground" />
                {t('test.uploads')} ({uploads.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {uploads.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">{t('test.noUploads')}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {uploads.map((upload) => (
                    <div
                      key={upload.id}
                      className="relative group cursor-pointer rounded-lg overflow-hidden bg-muted aspect-[4/3]"
                      onClick={() => upload.url && setSelectedMedia(upload)}
                    >
                      {upload.media_type === 'image' && upload.url ? (
                        <img
                          src={upload.url}
                          alt={displayFilename(upload)}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : upload.media_type === 'video' ? (
                        <div className="w-full h-full flex items-center justify-center bg-black/20">
                          <Play className="h-8 w-8 text-white/80" />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="h-6 w-6 text-muted-foreground/40" />
                        </div>
                      )}

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                        <div className="w-full p-1.5 text-white text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="truncate">{displayFilename(upload)}</p>
                          <p className="text-white/60">{formatBytes(upload.file_size)}</p>
                          {upload.camera_time && <p className="text-white/50">Captured: {formatTime(upload.camera_time)}</p>}
                          <p className="text-white/50">Uploaded: {formatTime(upload.server_time)}</p>
                        </div>
                      </div>

                      {/* Type badge */}
                      <div className="absolute top-1 right-1">
                        <span className={cn(
                          'text-[9px] font-medium px-1.5 py-0.5 rounded-full',
                          upload.media_type === 'video' ? 'bg-blue-500/80 text-white' : 'bg-white/80 text-black'
                        )}>
                          {upload.media_type === 'video' ? 'VIDEO' : 'IMG'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status history */}
          {statuses.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Signal className="h-4 w-4 text-muted-foreground" />
                  {t('test.statusHistory')} ({statuses.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className={cn('overflow-x-auto', d === 'expressive' && 'rounded-xl overflow-hidden')}>
                  <table className={cn('w-full',
                    d === 'current' && 'text-xs',
                    d === 'command' && 'font-mono text-[11px]',
                    d === 'saas' && 'text-[13px]',
                    d === 'expressive' && 'text-xs',
                  )}>
                    <thead>
                      <tr className={cn('border-b',
                        d === 'current' && 'bg-muted/30',
                        d === 'command' && 'bg-card sticky top-0',
                        d === 'saas' && 'bg-muted/30',
                        d === 'expressive' && 'bg-muted/20',
                      )}>
                        <th className={cn('text-left font-medium text-muted-foreground',
                          d === 'current' && 'px-3 py-2',
                          d === 'command' && 'px-3 py-1.5 text-[10px] uppercase tracking-wider',
                          d === 'saas' && 'px-3 py-1.5 text-[10px] uppercase tracking-wider',
                          d === 'expressive' && 'px-3 py-2',
                        )}>
                          {t('test.time')}
                        </th>
                        {[<Battery key="bat" className="h-3 w-3 inline" />, <Signal key="sig" className="h-3 w-3 inline" />, <Thermometer key="tmp" className="h-3 w-3 inline" />, <HardDrive key="hd" className="h-3 w-3 inline" />].map((icon, i) => (
                          <th key={i} className={cn('text-left font-medium text-muted-foreground',
                            d === 'current' && 'px-3 py-2',
                            d === 'command' && 'px-3 py-1.5 text-[10px] uppercase tracking-wider',
                            d === 'saas' && 'px-3 py-1.5 text-[10px] uppercase tracking-wider',
                            d === 'expressive' && 'px-3 py-2',
                          )}>
                            {icon}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={cn('divide-y divide-border/40', d === 'saas' && 'divide-y divide-border')}>
                      {statuses.map((s) => (
                        <tr key={s.id} className={cn(
                          d === 'current' && 'hover:bg-muted/20',
                          d === 'command' && 'hover:bg-accent/40',
                          d === 'saas' && 'hover:bg-muted',
                          d === 'expressive' && 'hover:bg-muted/20',
                        )}>
                          <td className={cn('text-muted-foreground whitespace-nowrap',
                            d === 'current' && 'px-3 py-2',
                            d === 'command' && 'px-3 py-1.5 font-mono',
                            d === 'saas' && 'px-3 py-1.5',
                            d === 'expressive' && 'px-3 py-2',
                          )}>
                            {new Date(s.server_time).toLocaleString()}
                          </td>
                          <td className={cn(d === 'current' ? 'px-3 py-2' : 'px-3 py-1.5', d === 'command' && 'tabular-nums')}>{s.battery != null ? `${s.battery}%` : '—'}</td>
                          <td className={cn(d === 'current' ? 'px-3 py-2' : 'px-3 py-1.5', d === 'command' && 'tabular-nums')}>{s.signal != null ? `${s.signal}/5` : '—'}</td>
                          <td className={cn(d === 'current' ? 'px-3 py-2' : 'px-3 py-1.5', d === 'command' && 'tabular-nums')}>{s.temperature != null ? `${s.temperature}°C` : '—'}</td>
                          <td className={cn(d === 'current' ? 'px-3 py-2' : 'px-3 py-1.5', d === 'command' && 'tabular-nums')}>{s.sd_used_mb != null && s.sd_total_mb != null ? `${s.sd_used_mb}/${s.sd_total_mb} MB` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page — switches between list and detail
// ---------------------------------------------------------------------------

export function TestCamerasPage() {
  const { t } = useTranslation('cameras')
  const d = useDirectionStore((s) => s.direction)
  const [selectedImei, setSelectedImei] = useState<string | null>(null)

  return (
    <div className="space-y-4 sm:space-y-6">
      {!selectedImei ? (
        <>
          <div className="animate-fade-up">
            <h2 className={cn('text-xl sm:text-2xl font-semibold tracking-tight', d === 'expressive' && 'font-display')}>{t('test.pageTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('test.pageDescription')}</p>
          </div>
          <CameraList onSelect={setSelectedImei} />
        </>
      ) : (
        <CameraDetail imei={selectedImei} onBack={() => setSelectedImei(null)} />
      )}
    </div>
  )
}
